import React, { useMemo } from "react";
import Grid from "@mui/material/Grid";
import { FormControl, Skeleton } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Select, DatePicker, ConfigProvider, theme as antdTheme } from "antd";
import { useAxiosPrivate } from "hooks/useAxiosPrivate";
import { getLoggedInUser } from "service/AuthService";
import { HTTP_STATUS, HTTP_STATUS_MESSAGE } from "constants/HttpStatus";
import { useMaterialUIController } from "context";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import reportsBarChartData from "layouts/dashboard/data/reportsBarChartData";
import reportsLineChartData from "layouts/dashboard/data/reportsLineChartData";
import MDTypography from "components/MDTypography";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import { date } from "yup";
const dashboard = () => {
  const { sales, tasks } = reportsLineChartData;
  const { RangePicker } = DatePicker;
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [teamEmployeeList, setTeamEmployeeList] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState({});
  const [teamMember, setTeamMember] = useState(null);
  const [selectionSource, setSelectionSource] = useState("init");
  // "init" | "reset" | "dropdown"

  const [leadsData, setLeadsData] = useState([]);
  const [activityData, setActivityData] = useState([]);
  const [dates, setDates] = useState([dayjs(), dayjs()]);
  const [chartdDates, setChartDates] = useState(null);
  const [apiParams, setApiParams] = useState({
    startTime: dayjs().startOf("day").unix(),
    endTime: dayjs().endOf("day").unix(),
    forTeams: "",
    employeeId: "",
  });
  const [loading, setLoading] = useState(false);

  /* -------------------- ICON & COLOR MAP -------------------- */

  const iconMapLead = {
    Cold: "ac_unit",
    Hot: "whatshot",
    Warm: "wb_sunny",
    Marketing: "campaign",
    Onhold: "pause_circle",
    Win: "emoji_events",
  };

  const colorMapLead = {
    Cold: "info",
    Hot: "error",
    Warm: "warning",
    Marketing: "secondary",
    Onhold: "dark",
    Win: "success",
  };

  const iconMapTask = {
    callDetails: "call",
    followUp: "follow_the_signs",
    meeting: "groups",
    overdue: "alarm_on",
    siteVisit: "location_on",
  };

  const colorMapTask = {
    callDetails: "primary",
    followUp: "info",
    meeting: "warning",
    overdue: "error",
    siteVisit: "success",
  };

  const convertLeadData = (leadObj = {}) =>
    Object.entries(leadObj).map(([key, value]) => ({
      title: key,
      count: value,
      icon: iconMapLead[key],
      color: colorMapLead[key],
    }));

  const convertTaskData = (taskObj = {}) =>
    Object.entries(taskObj).map(([key, value]) => ({
      title: value.displayName,
      count: value.total,
      done: value.done,
      icon: iconMapTask[key],
      color: colorMapTask[key],
    }));

  const calculateForTeams = ({ teamEmployee, employeeId, selectionSource }) => {
    if (!teamEmployee || Object.keys(teamEmployee).length <= 1) return false;
    const isSelf = teamEmployee[employeeId]?.self === true;
    if ((selectionSource === "init" || selectionSource === "reset") && isSelf) {
      return true;
    }
    if (selectionSource === "dropdown" && isSelf) {
      return false;
    }
    return true;
  };

  const handleTeamMemberChange = (employeeId) => {
    const startTime = apiParams.startTime;
    const endTime = apiParams.endTime;
    setSelectedEmployee({ ...teamEmployeeList[employeeId], id: employeeId });
    getData(employeeId, startTime, endTime, "dropdown");
    getTaskChartData(null, null, employeeId, "dropdown");
  };

  const handleReset = () => {
    const user = getLoggedInUser();
    const startTime = dayjs().startOf("day").unix();
    const endTime = dayjs().endOf("day").unix();
    setSelectedEmployee({});
    setDates([dayjs(), dayjs()]);
    getData(user.empId, startTime, endTime, "reset");
    getTaskChartData(null, null, user?.empId, "reser");
    setChartDates(null);
  };

  const handleDateChange = (dates) => {
    if (!dates) return;
    const startTime = dates[0].startOf("day").unix();
    const endTime = dates[1].endOf("day").unix();
    setDates(dates);
    getData(apiParams.employeeId, startTime, endTime, "init");
  };

  const getTeamEmployeeData = async (employedId) => {
    try {
      const user = getLoggedInUser();
      const res = await axiosPrivate.get(`om/employee?id=${employedId ? employedId : user?.empId}`);
      if (res.status === HTTP_STATUS.OK && res.data.status === HTTP_STATUS_MESSAGE.OK) {
        const transformed = {};
        Object.entries(res.data.data || {}).forEach(([id, name]) => {
          transformed[id] = { name, self: false };
        });

        transformed[user.empId] = {
          name: `${user.employeeName} (self)`,
          self: true,
        };

        setTeamEmployeeList(transformed);
        return transformed;
      } else {
        setTeamEmployeeList({});
        return {};
      }
    } catch {
      setTeamEmployeeList({});
      return {};
    }
  };

  useEffect(() => {
    const user = getLoggedInUser();
    const params = Object.fromEntries([...searchParams]);
    const employeeId = params?.employeeId || user?.empId;
    const startTime = params?.startTime
      ? parseInt(params?.startTime)
      : dayjs().startOf("day").unix();
    const endTime = params?.endTime ? parseInt(params?.endTime) : dayjs().endOf("day").unix();
    setApiParams({ startTime, endTime, employeeId });
    setDates([dayjs.unix(startTime), dayjs.unix(endTime)]);
    getData(employeeId, startTime, endTime, "init");
    getTaskChartData(null, null, employeeId, "init");
  }, []);

  const getData = async (employeeId, startTime, endTime, source) => {
    const teamEmployee = await getTeamEmployeeData(employeeId);
    const forTeams = calculateForTeams({
      teamEmployee,
      employeeId,
      selectionSource: source,
    });
    const dt = teamEmployee;
    getDashboardData({
      empId: employeeId,
      startTime: startTime,
      endTime: endTime,
      forTeams,
    });
    setApiParams({ startTime, endTime, employeeId, forTeams });
    setSearchParams({
      employeeId,
      startTime: startTime.toString(),
      endTime: endTime.toString(),
      forTeams: forTeams.toString(),
    });
  };

  const getDashboardData = async ({ empId, startTime, endTime, forTeams }) => {
    setLoading(true);
    try {
      const res = await axiosPrivate.post("om/dashboard", {
        teamEmployeeId: empId,
        startTime,
        endTime,
        forTeams,
      });

      if (res.status === HTTP_STATUS.OK && res.data.status === HTTP_STATUS_MESSAGE.OK) {
        setLeadsData(convertLeadData(res.data.data.lead));
        setActivityData(convertTaskData(res.data.data.tasks));
      }
    } finally {
      setLoading(false);
    }
  };

  const skeletonArray = Array.from({ length: 6 });

  const navigateToActivity = ({ status, startTime, endTime, selectedEmployee }) => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (startTime) params.set("startTime", startTime);
    if (endTime) params.set("endTime", endTime);

    // pass employee ONLY if selected
    if (selectedEmployee?.id) {
      params.set("employeeId", selectedEmployee?.id);
      params.set("employeeName", selectedEmployee?.name);
    } else {
      const user = getLoggedInUser();
      params.set("employeeId", user?.empId);
      params.set("employeeName", user?.employeeName);
    }

    navigate(`/dashboard/activity?${params.toString()}`);
  };

  const [state, setState] = useState({
    series: [
      {
        name: "Net Profit",
        data: [44, 55, 57],
      },
      {
        name: "Revenue",
        data: [76, 85, 101],
      },
      {
        name: "Free Cash Flow",
        data: [35, 41, 36],
      },
    ],
    options: {
      chart: {
        type: "bar",
        height: 350,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "55%",
          borderRadius: 5,
          borderRadiusApplication: "end",
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 2,
        colors: ["transparent"],
      },
      xaxis: {
        categories: ["Feb", "Mar", "Apr"],
      },
      yaxis: {
        title: {
          text: "$ (thousands)",
        },
      },
      fill: {
        opacity: 1,
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return "$ " + val + " thousands";
          },
        },
      },
    },
  });

  const colors = [
    "#008FFB",
    "#00E396",
    "#FEB019",
    "#FF4560",
    "#775DD0",
    "#546E7A",
    "#26a69a",
    "#D10CE8",
  ];
  const color = [
    "#008FFB",
    "#00E396",
    "#FEB019",
    "#FF4560",
    "#775DD0",
    "#546E7A",
    "#26a69a",
    "#D10CE8",
  ];

  const [taskChartData, setTaskChartData] = useState(null);
  const [taskChartDataLoader, setTaskChartDataLoader] = useState(false);
  const getTaskChartData = async (startTime, endTime, empId, source) => {
    const teamEmployee = await getTeamEmployeeData(empId);
    const forTeams = calculateForTeams({
      teamEmployee,
      empId,
      selectionSource: source,
    });
    const options = {
      method: "POST",
      url: "om/external/taskChart",
      data: {
        forTeams: forTeams,
        teamEmployeeId: empId,
      },
    };

    if (startTime) {
      options.data.startTime = startTime;
    }
    if (endTime) {
      options.data.endTime = endTime;
    }

    setTaskChartDataLoader(true);
    axiosPrivate(options)
      .then((response) => {
        const apiData = response?.data?.data;
        const categories = Object.keys(apiData);
        const series = [
          {
            name: "Positive",
            data: categories.map((key) => apiData[key].Positive),
          },
          {
            name: "Negative",
            data: categories.map((key) => apiData[key].Negative),
          },
          {
            name: "Neutral",
            data: categories.map((key) => apiData[key].Neutral),
          },
          {
            name: "Cancelled",
            data: categories.map((key) => apiData[key].Cancelled),
          },
        ];
        setTaskChartData({
          series,
          options: {
            chart: {
              type: "bar",
              height: 350,
            },
            plotOptions: {
              bar: {
                horizontal: false,
                columnWidth: "55%",
                borderRadius: 5,
                borderRadiusApplication: "end",
              },
            },
            dataLabels: { enabled: false },
            stroke: { show: true, width: 2, colors: ["transparent"] },
            xaxis: { categories },
            fill: { opacity: 1 },
            tooltip: {
              y: {
                formatter: (val) => val,
              },
            },
          },
        });
        setTaskChartDataLoader(false);
      })
      .catch((error) => {
        console.log(error);
        setTaskChartDataLoader(false);
      });
  };
  const handleChartDateChange = (dates) => {
    if (!dates) return;
    const startTime = dates.startOf("month").unix();
    const endTime = dates.endOf("month").unix();
    setChartDates(dates);
    getTaskChartData(startTime, endTime, apiParams?.employeeId, "init");
  };

  const disabledDate = (current) => {
    if (!current) return false;
    const today = dayjs();
    const oneMonthAgo = today.subtract(1, "month");
    if (current > today) return true;
    if (current < oneMonthAgo.startOf("day")) return true;
    return false;
  };

  return (
    <DashboardLayout loading={loading || taskChartDataLoader}>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <MDBox fontWeight="bold" fontSize={24}>
            Activity
          </MDBox>

          <MDBox display="flex" gap={2} flexWrap="wrap">
            <FormControl sx={{ minWidth: 220 }}>
              <ConfigProvider
                theme={{
                  algorithm: darkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                  components: {
                    Select: {
                      colorBgContainer: darkMode ? "#1a2035" : "#fff", // input box
                      colorText: darkMode ? "#fff" : "#000",
                      colorBorder: darkMode ? "#475894" : "#d9d9d9",
                      colorPrimary: darkMode ? "#1890ff" : "#1890ff",
                      optionActiveBg: darkMode ? "#1f2937" : "#f5f5f5",
                      optionSelectedBg: darkMode ? "#1a243bff" : "#e6f4ff",
                    },
                  },
                }}
              >
                <Select
                  size="large"
                  placeholder={`${
                    selectedEmployee?.name
                      ? "Selected " + selectedEmployee?.name
                      : " Select Team Member"
                  }`}
                  value={teamMember}
                  onChange={handleTeamMemberChange}
                  options={
                    teamEmployeeList &&
                    Object.entries(teamEmployeeList).map(([id, d]) => ({
                      label: d.name,
                      value: id,
                    }))
                  }
                />
              </ConfigProvider>
            </FormControl>

            <MDButton onClick={handleReset} variant="outlined" size="small" color={"secondary"}>
              Reset
            </MDButton>

            <ConfigProvider
              theme={{
                algorithm: darkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                components: {
                  DatePicker: {
                    colorBgContainer: darkMode ? "#1a2035" : "#fff",
                    colorText: darkMode ? "#fff" : "#000",
                    colorBorder: darkMode ? "#475894" : "#d9d9d9",
                    colorBgElevated: darkMode ? "#1f2937" : "#fff",
                    colorPrimary: darkMode ? "#1890ff" : "#1890ff",
                  },
                },
              }}
            >
              <RangePicker value={dates} onChange={handleDateChange} allowClear={false} />
            </ConfigProvider>
          </MDBox>
        </MDBox>

        <Grid container spacing={3} py={4}>
          {(loading ? skeletonArray : activityData).map((item, index) => (
            <Grid key={index} item xs={12} sm={6} md={3} lg={3}>
              <MDBox
                mb={1.5}
                onClick={() => {
                  if (!loading) {
                    if (item?.title != "Connected/Dialed") {
                      const params = {
                        status: item?.title,
                        startTime: apiParams?.startTime,
                        endTime: apiParams?.endTime,
                        selectedEmployee,
                      };
                      navigateToActivity(params);
                    }
                  }
                }}
              >
                <ComplexStatisticsCard
                  color={item?.color || "info"}
                  icon={item?.icon || "task"}
                  title={item?.title || ""}
                  count={item?.count || 0}
                  percentage={{ color: "success" }}
                  loading={loading}
                  done={item?.done}
                />
              </MDBox>
            </Grid>
          ))}
        </Grid>

        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={3}>
                <Card sx={{ padding: "1rem" }}>
                  {taskChartData && !taskChartDataLoader && (
                    <>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        py={1}
                      >
                        <MDTypography variant="button" fontWeight="bold" color={"text"}>
                          Completed vs Cancelled (Default 30 days)
                        </MDTypography>
                        <MDBox>
                          <ConfigProvider
                            theme={{
                              algorithm: darkMode
                                ? antdTheme.darkAlgorithm
                                : antdTheme.defaultAlgorithm,
                              components: {
                                DatePicker: {
                                  colorBgContainer: darkMode ? "#1a2035" : "#fff",
                                  colorText: darkMode ? "#fff" : "#000",
                                  colorBorder: darkMode ? "#475894" : "#d9d9d9",
                                  colorBgElevated: darkMode ? "#1f2937" : "#fff",
                                  colorPrimary: darkMode ? "#1890ff" : "#1890ff",
                                },
                              },
                            }}
                          >
                            {/* <RangePicker
                              value={chartdDates}
                              onChange={handleChartDateChange}
                              allowClear={false}
                              maxRange={{ months: 1 }}
                              disabledDate={disabledDate}
                            /> */}

                            <DatePicker
                              picker="month"
                              allowClear={false}
                              value={chartdDates}
                              onChange={handleChartDateChange}
                            />
                          </ConfigProvider>
                        </MDBox>
                      </MDBox>
                      {taskChartDataLoader && (
                        <Skeleton variant="rectangular" height={230} sx={{ borderRadius: 2 }} />
                      )}
                      <ReactApexChart
                        options={taskChartData?.options}
                        series={taskChartData?.series}
                        type="bar"
                        height={216}
                      />
                    </>
                  )}
                </Card>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
};

export default dashboard;
